export interface AwsDefaults {
	id: string;
	createdAt: string;
	updatedAt: string;
	group: string;
}

export interface AwsItems<T> {
	items: T[];
	nextToken?: string;
}

// must be type for the intersection to work
export type UpdateObj = Record<string, FieldInstance> & { _nav?: string };

export interface FieldContinuationDataForResolver extends AwsDefaults {
	continuationActions: string[];
	fieldTemplateId: string;
	formTemplateId: string;
	instanceToContinueOn: string;
	parentFormTemplateId: string;
	readGroups: string[];
}

export interface FieldContinuationData extends FieldContinuationDataForResolver {
	fieldTemplate?: FieldTemplate;
	formTemplate?: FormTemplate;
}

export interface Option extends AwsDefaults {
	fieldTemplateId: string;
	formTemplate: string;
	label: string;
	value?: string;
	docId?: string;
	exportData?: string[];
	exportActions?: string[];
	linkedId?: string;
	deleted?: boolean;
	readGroups: string[];
}

export type IdentityRole = "temporary" | "resource" | "";

export interface FieldTemplateForResolver extends AwsDefaults {
	action: string[];
	actionData: string[];
	deleted?: boolean;
	exportData: string[];
	exportType: string;
	exportActions: string[];
	formTemplate: string;
	header: string;
	inputType: string;
	optionsOrder?: string[];
	rowOrder?: string[];
	tableField?: string;
	docId: string;
	continuationDataId?: string;
	placeholder?: string;
	pixelFollowing?: number;
	pixelInitial?: number;
	rowsAllowed?: number;
	totalExtraLines?: number;
	font?: string;
	fontSize?: number;
	bold?: boolean;
	rollLinkId?: string;
	version?: string;
	baseFieldTemplate?: string;
	readGroups: string[];
	csvKey?: string;
	identityRole?: IdentityRole;
}

export interface FieldTemplate extends FieldTemplateForResolver {
	tableFields?: AwsItems<FieldTemplate>;
	options?: AwsItems<Option>;
	continuationData?: FieldContinuationData;
}

export interface FormTemplateForResolver extends AwsDefaults {
	createdBy?: string;
	deleted?: boolean;
	description?: string;
	exportInstruction?: string;
	exportType?: string;
	fieldsOrder?: string[];
	formTemplateGroup: string;
	imagesPerPage?: number;
	projectId?: string;
	publish?: boolean;
	template: string;
	templateName?: string;
	type?: string;
	updatedBy?: string;
	version?: string;
	versionName?: string;
	readGroups: string[];
}

export interface FormTemplate extends FormTemplateForResolver {
	fieldTemplates?: AwsItems<FieldTemplate>;
}

export interface FormGroupForResolver extends AwsDefaults {
	users?: string[];
	name: string;
	projectId: string;
	type: string;
	formOrder: string[];
	group: string;
	syncedAt?: string;
	lastUpdatedBy?: string;
}

export interface FormGroup extends FormGroupForResolver {
	fieldInstances?: AwsItems<FieldInstance>;
	formInstances?: AwsItems<FormInstance>;
	offline?: boolean;
	offlineAndOnline?: boolean;
}

export interface FormInstanceForResolver extends AwsDefaults {
	formTemplateId: string;
	formGroup: string;
	exportInstruction?: string;
	group: string;
}

export interface FormInstance extends FormInstanceForResolver {
	formTemplate?: FormTemplate;
	fieldInstances: AwsItems<FieldInstance>;
}

export interface FieldInstanceForResolver extends AwsDefaults {
	formInstance?: string | null;
	formGroup: string;
	fieldTemplateId: string | null;
	value: string;
	altInstanceId?: string;
	imageId?: string;
	tableField?: string | null;
	fieldsOrder: string[] | null;
	sortingPriority?: string | null;
	notes: string;
	forceUpdateData?: string;
	exportInstruction?: string;
	authors: string[];
	editing: string;
	lastAuthor?: string;
	group: string;
	/**
	 * This will only appear on offline fields. DO NOT ATTACH TO FIELD INSTANCE AND TRY TO PASS TO BATCH RESOLVER
	 */
	touched?: number;
}

export interface FieldInstance extends FieldInstanceForResolver {
	fieldTemplate?: FieldTemplate | null;
	altInstance?: FieldInstanceForResolver | null;
	image?: Image | null;
	tableFields: AwsItems<FieldInstance>;
}

export interface Image extends AwsDefaults {
	key: string | { files: File[] };
	formGroup: string;
	project: string;
	rotation: string;
	dpi: string;
	aspectX: string;
	aspectY: string;
	group: string;
}

export interface UserForResolver extends AwsDefaults {
	username: string;
	email?: string;
	hideTutorial?: boolean;
	isDeveloper?: boolean;
	lightMode?: boolean;
	roleIds: string[];
}

export interface User extends UserForResolver {
	messages?: UserMessage[];
	roles: Role[];
}

export interface RoleForResolver extends AwsDefaults {
	name: string;
	description: string;
	permissionIds: string[];
	readGroups: string[];
}

export interface Role extends RoleForResolver {
	permissions: Permission[];
}

export interface Permission extends AwsDefaults {
	name: string;
	value: string;
	description: string;
}

export interface UserMessage extends AwsDefaults {
	userId: string;
	messageId: string;
	user?: User;
	message?: Message;
	read?: boolean;
	lightMode?: boolean;
	group: string;
}

export interface Message extends AwsDefaults {
	subject?: string;
	from?: string;
	author?: string;
	message?: string;
	projects?: [string];
	group: string;
}

export interface ProjectForResolver extends AwsDefaults {
	users?: string[];
	name: string;
	description: string;
	public?: boolean;
	forceUpdateData?: string;
	group: string;
}

export interface Project extends ProjectForResolver {
	/** formTemplates assigned to project will be automatically synced to device for offline use*/
	syncFormTemplates?: string[];
	formGroups: AwsItems<FormGroup>;
	/**
	 * This only exists in state, for the purpose of knowing if the project needs to be the one that is on the "online" side of the
	 * sync menu
	 */
	sync?: boolean;
	/**
	 * This only exists in state, for the purpose of knowing if the project exists only offline. That way, you can select this
	 * project within the sync menu
	 */
	offline?: boolean;
	/**
	 * This exists only in state, for the purpose of knowing if the project exists only online. If you delete a project while online
	 * with this flag, the project then gets an offline flag because it still exists offline
	 */
	offlineAndOnline?: boolean;
}

export interface ImageBinary {
	key: string;
	binary: File;
}

export interface JsonStructure {
	continuationDatas: FieldContinuationDataForResolver[];
	fieldInstances: FieldInstanceForResolver[];
	fieldTemplates: FieldTemplateForResolver[];
	formGroups: FormGroupForResolver[];
	formInstances: FormInstanceForResolver[];
	formTemplates: FormTemplateForResolver[];
	images: Image[];
	options: Option[];
	projects: ProjectForResolver[];
	revisions: Revision[];
	version: number;
}

export const CURRENT_JSON_VERSION = 1;

export interface Revision extends AwsDefaults {
	fieldInstance: string;
	formGroup: string;
	value: string;
	author: string;
	group: string;
}

export type Source = "json" | "local" | "remote";

export interface SyncConfig {
	/** Where the sync/merge request is going */
	destination: Source;
	/** Whether or not the user is online */
	online: boolean;
	/** When syncing from remote to local, whether or not the image binaries should be saved */
	savePhotos?: boolean;
	/** Where the sync/merge request is originating */
	source: Source;
}

export interface AutoSyncConfig {
	id: string;
	baseTemplate: string;
	versionIds: string[];
	createdAt: string;
	updatedAt: string;
	autoSync: boolean;
}

export interface ProjectConfig {
	id: string;
	projectId: string;
	userId: string;
	createdAt: string;
	updatedAt: string;
}

export interface BatchDeleteInput {
	id: string;
}

export type RequirePropAsType<T, K extends keyof T, Type> = T & { [P in K]-?: Type };

interface MFAOption {
	DeliveryMedium: "SMS" | "Email";
	AttributeName: string;
}

interface CustomAttributes {
	Name: string;
	Value: string;
}

export interface CognitoUser {
	Username: string;
	UserCreateDate: string;
	UserLastModifiedDate: string;
	Enabled: boolean;
	UserStatus: "UNCONFIRMED" | "CONFIRMED" | "ARCHIVED" | "COMPROMISED" | "UNKNOWN" | "RESET_REQUIRED" | "FORCE_CHANGE_PASSWORD" | "EXTERNAL_PROVIDER";
	MFAOptions?: MFAOption[] | null;
	/**
	 * Attributes associated with the Cognito user.
	 *
	 * **Important:** There will always be at least 3 required attributes:
	 *
	 * 1. `sub`: The unique identifier for the user. **This is the id of this cognito user's user document in Dynamo**
	 *    ```json
	 *    { "Name": "sub", "Value": "string" }
	 *    ```
	 *
	 * 2. `email`: The user's email address.
	 *    ```json
	 *    { "Name": "email", "Value": "string" }
	 *    ```
	 *
	 * 3. `email_verified`: Whether the email has been verified. **NOTE THAT THE VALUE IS STRINGIFIED**
	 *    ```json
	 *    { "Name": "email_verified", "Value": "true" | "false" }
	 *    ```
	 *
	 * The order of attributes is not guaranteed, but these three will always be present.
	 */
	Attributes: CustomAttributes[];
}

// Analytics Event Types
export interface AnalyticsEventForResolver {
	id: string;
	eventType: string; // From EventType enum
	userId: string;
	groupId: string;
	timestamp: string; // ISO 8601 - when event occurred
	sessionId: string;
	deviceType: string; // From DeviceType enum
	metadata?: Record<string, any>; // Optional, keep minimal
	appVersion?: string;
	offlineQueued: number; // 1 = needs sync, 0 = synced (can delete)
	createdAt: string; // ISO 8601
	updatedAt: string; // ISO 8601
}
